/**
 * Copyright (c) 2021 lingdocs.com
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import { concatPsString, firstPhonetics, makePsString, psStringEquals, removeEndingL, yulEndingInfinitive, removeRetroflexR, inflectYey, unisexInfToObjectMatrix, complementInflects, beginsWithDirectionalPronoun, checkForOoPrefix, removeStartingTick, ensureShortWurShwaShift, choosePersInf } from "./p-text-helpers";
import { accentOnFront, accentOnNFromEnd, removeAccents } from "./accent-helpers";
import { inflectWord } from "./pashto-inflector";
import { stativeAux } from "./irregular-conjugations";
import { presentParticipleSuffix } from "./grammar-units";
import { dynamicAuxVerbs } from "./dyn-comp-aux-verbs";
import { getPersonInflectionsKey, getPersonNumber, spaceInForm, getAuxTransitivity, chooseParticipleInflection } from "./misc-helpers";
var eyEndingUnaccented = {
  p: "ی",
  f: "ey"
};
/**
 * Compiles the base information (roots, stems etc.) needed in order
 * to make all the conjugations. This is the first step of creating
 * the conjugations.
 *
 * @param entry - the dictionary entry for the verb
 * @param complement - the dictioanry entry for the complement of the verb if compound
 */

export function getVerbInfo(entry, complement) {
  var type = getType(entry);

  if (type === "transitive or grammatically transitive simple") {
    return {
      type: "transitive or grammatically transitive simple",
      transitive: getVerbInfo(Object.assign(Object.assign({}, entry), {
        c: entry.c.replace("trans./gramm. trans.", "trans.")
      })),
      grammaticallyTransitive: getVerbInfo(Object.assign(Object.assign({}, entry), {
        c: entry.c.replace("trans./gramm. trans.", "gramm. trans.")
      }))
    };
  }

  var transitivity = getTransitivity(entry);

  if (type !== "simple") {
    if (!complement) {
      throw new Error("complement required for compound verb");
    }

    if (type === "dynamic compound") {
      return getDynamicCompoundInfo(entry, complement);
    }

    if (type === "dynamic or stative compound") {
      return {
        type: "dynamic or stative compound",
        transitivity: transitivity,
        dynamic: getDynamicCompoundInfo(Object.assign(Object.assign({}, entry), {
          c: entry.c.replace("dyn./stat.", "dyn.")
        }), complement),
        stative: getVerbInfo(Object.assign(Object.assign({}, entry), {
          c: entry.c.replace("dyn./stat.", "stat.")
        }), complement)
      };
    }

    if (type === "dynamic or generative stative compound") {
      return {
        type: "dynamic or generative stative compound",
        transitivity: transitivity,
        dynamic: getDynamicCompoundInfo(Object.assign(Object.assign({}, entry), {
          c: entry.c.replace("gen. stat./dyn.", "dyn.")
        }), complement),
        stative: getGenerativeStativeCompoundVerbInfo(Object.assign(Object.assign({}, entry), {
          c: entry.c.replace("gen. stat./dyn.", "gen. stat.")
        }), complement)
      };
    }

    if (type === "generative stative compound") {
      return getGenerativeStativeCompoundVerbInfo(entry, complement);
    }
  }

  var comp = complement ? ensureUnisexInflections(complement) : undefined;
  var root = getVerbRoots(entry, transitivity, comp);
  var stem = getVerbStems(entry, root, transitivity, comp);
  var infinitive = "mascSing" in root.imperfective ? root.imperfective.mascSing.long : root.imperfective.long;
  var yulEnding = yulEndingInfinitive(infinitive);
  var participle = getParticiple(entry, stem, infinitive, transitivity, comp);
  var idiosyncraticThirdMascSing = getIdiosyncraticThirdMascSing(entry);
  var baseInfo = Object.assign({
    transitivity: transitivity,
    yulEnding: yulEnding,
    root: root,
    stem: stem,
    participle: participle
  }, idiosyncraticThirdMascSing ? {
    idiosyncraticThirdMascSing: idiosyncraticThirdMascSing
  } : {});

  if (type === "stative compound") {
    return Object.assign(Object.assign({}, baseInfo), {
      type: type,
      complement: comp
    });
  }

  return Object.assign(Object.assign({}, baseInfo), {
    type: type
  });
}

function getGenerativeStativeCompoundVerbInfo(entry, comp, forceSingular) {
  var transitivity = getTransitivity(entry);
  var transitivityNoGrammTrans = transitivity === "grammatically transitive" ? "transitive" : transitivity;
  var yulEnding = null;
  var objComplement = getObjComplementInfo(entry, comp, forceSingular);
  var auxVerb = stativeAux[transitivityNoGrammTrans];
  var compUsed = objComplement.plural ? objComplement.plural : makePsString(objComplement.entry.p, firstPhonetics(objComplement.entry.f));
  var bases = {
    stem: {
      imperfective: auxVerb.info.stem.imperfective,
      perfective: auxVerb.info.stem.perfective
    },
    root: {
      imperfective: auxVerb.info.root.imperfective,
      perfective: auxVerb.info.root.perfective
    },
    participle: {
      present: auxVerb.info.participle.present,
      past: chooseParticipleInflection(inflectYey("mascSing" in auxVerb.info.participle.past // purely for type saftey, will not have mascSing
      // in a non stative compound verb

      /* istanbul ignore next */
      ? auxVerb.info.participle.past.mascSing : auxVerb.info.participle.past), objComplement.person)
    }
  };
  var perfectiveStem = concatPsString(compUsed, " ", bases.stem.perfective);
  var stem = {
    imperfective: concatPsString(compUsed, " ", bases.stem.imperfective),
    perfective: perfectiveStem,
    perfectiveSplit: splitPerfective(perfectiveStem, 0, 0, true)
  };
  var perfectiveRoot = concatPsString(compUsed, " ", bases.root.perfective);
  var root = {
    imperfective: concatPsString(compUsed, " ", bases.root.imperfective),
    perfective: perfectiveRoot,
    perfectiveSplit: splitPerfective(perfectiveRoot, 0, 0, true)
  };
  var participle = {
    present: concatPsString(compUsed, " ", auxVerb.info.participle.present),
    past: concatPsString(compUsed, " ", bases.participle.past)
  };
  return Object.assign({
    type: "generative stative compound",
    transitivity: transitivity,
    yulEnding: yulEnding,
    stem: stem,
    root: root,
    participle: participle,
    objComplement: objComplement
  }, objComplement.plural ? {
    singularForm: getGenerativeStativeCompoundVerbInfo(entry, comp, true)
  } : {});
}

function getDynamicCompoundInfo(entry, comp, forceSingular) {
  var transitivity = getTransitivity(entry);
  var yulEnding = null;
  var objComplement = getObjComplementInfo(entry, comp, forceSingular);
  var auxVerb = getDynamicAuxVerb(entry);
  var auxVerbInfo = getVerbInfo(auxVerb.entry, auxVerb.complement);
  var compUsed = objComplement.plural ? objComplement.plural : makePsString(objComplement.entry.p, firstPhonetics(objComplement.entry.f));
  var bases = auxVerbInfo.type === "stative compound" ? getObjectMatchingBases(auxVerbInfo, objComplement.person) : {
    stem: Object.assign({
      imperfective: auxVerbInfo.stem.imperfective,
      perfective: auxVerbInfo.stem.perfective
    }, auxVerbInfo.stem.perfectiveSplit ? {
      perfectiveSplit: auxVerbInfo.stem.perfectiveSplit
    } : {}),
    root: Object.assign({
      imperfective: auxVerbInfo.root.imperfective,
      perfective: auxVerbInfo.root.perfective
    }, auxVerbInfo.root.perfectiveSplit ? {
      perfectiveSplit: auxVerbInfo.root.perfectiveSplit
    } : {}),
    participle: {
      present: auxVerbInfo.participle.present,
      past: chooseParticipleInflection(inflectYey("mascSing" in auxVerbInfo.participle.past // purely for type saftey, will not have mascSing
      // in a non stative compound verb

      /* istanbul ignore next */
      ? auxVerbInfo.participle.past.mascSing : auxVerbInfo.participle.past), objComplement.person)
    }
  };
  var stem = Object.assign({
    imperfective: concatPsString(compUsed, " ", bases.stem.imperfective),
    perfective: concatPsString(compUsed, " ", bases.stem.perfective)
  }, bases.stem.perfectiveSplit ? {
    perfectiveSplit: makeDynamicPerfectiveSplit(compUsed, bases.stem.perfectiveSplit)
  } : {});
  var root = Object.assign({
    imperfective: concatPsString(compUsed, " ", bases.root.imperfective),
    perfective: concatPsString(compUsed, " ", bases.root.perfective)
  }, bases.root.perfectiveSplit ? {
    perfectiveSplit: makeDynamicPerfectiveSplit(compUsed, bases.root.perfectiveSplit)
  } : {});
  var participle = {
    present: concatPsString(compUsed, " ", auxVerbInfo.participle.present),
    past: concatPsString(compUsed, " ", bases.participle.past)
  };

  var makeIntransitiveFormOfEntry = function makeIntransitiveFormOfEntry(e) {
    return Object.assign(Object.assign({}, e), {
      p: e.p.replace("کول", "کېدل"),
      e: e.e.replace("to do", "to become"),
      f: e.f.replace(/kaw[u|ú]l/, "kedul"),
      c: "v. intrans. dyn. comp."
    });
  };

  var intransitiveFormEntry = transitivity === "transitive" && auxVerb.entry.p === "کول" ? makeIntransitiveFormOfEntry(entry) : null;
  return Object.assign(Object.assign(Object.assign({
    type: "dynamic compound",
    transitivity: transitivity,
    yulEnding: yulEnding,
    stem: stem,
    root: root,
    participle: participle,
    objComplement: objComplement,
    auxVerb: auxVerb.entry
  }, auxVerb.complement ? {
    auxVerbComplement: auxVerb.complement
  } : {}), objComplement.plural ? {
    singularForm: getDynamicCompoundInfo(entry, comp, true)
  } : {}), intransitiveFormEntry ? {
    intransitiveForm: getDynamicCompoundInfo(intransitiveFormEntry, comp)
  } : {});
}

function getObjectMatchingBases(auxInfo, person) {
  var key = getPersonInflectionsKey(person);

  var getBase = function getBase(x) {
    return "mascSing" in x ? x[key] : x;
  };

  return {
    stem: Object.assign({
      imperfective: getBase(auxInfo.stem.imperfective),
      perfective: getBase(auxInfo.stem.perfective)
    }, auxInfo.stem.perfectiveSplit ? {
      perfectiveSplit: choosePersInf(auxInfo.stem.perfectiveSplit, key)
    } : {}),
    root: Object.assign({
      imperfective: getBase(auxInfo.root.imperfective),
      perfective: getBase(auxInfo.root.perfective)
    }, auxInfo.root.perfectiveSplit ? {
      perfectiveSplit: choosePersInf(auxInfo.root.perfectiveSplit, key)
    } : {}),
    participle: {
      present: getBase(auxInfo.participle.present),
      past: getBase(auxInfo.participle.past)
    }
  };
}

function getObjComplementInfo(entry, complement, forceSingular) {
  var complementInEntry = makePsString(entry.p.split(" ")[0], entry.f.split(" ")[0]);
  var complementEntry = Object.assign(Object.assign({}, complement), {
    f: firstPhonetics(complement.f)
  });
  var usesSeperatePluralForm = !forceSingular && !psStringEquals(makePsString(complementInEntry.p, removeAccents(complementInEntry.f)), makePsString(complementEntry.p, removeAccents(complementEntry.f)));
  return Object.assign(Object.assign({
    entry: complementEntry
  }, usesSeperatePluralForm ? {
    plural: complementInEntry
  } : {}), {
    person: getComplementPerson(complement, usesSeperatePluralForm)
  });
}

function getTransitivity(entry) {
  if (!entry.c) {
    throw new Error("No part of speech info");
  }

  if (entry.c.includes("gramm. trans.")) {
    return "grammatically transitive";
  }

  if (entry.c.includes("intrans.")) {
    return "intransitive";
  }

  return "transitive";
}

function getType(entry) {
  // error will have thrown before on the getTransitivity function if missing entry.c

  /* istanbul ignore if */
  if (!entry.c) {
    throw new Error("No part of speech info");
  }

  if (entry.c.includes(" trans./gramm. trans.")) {
    return "transitive or grammatically transitive simple";
  }

  if (entry.c.includes(" gen. stat. comp.")) {
    return "generative stative compound";
  }

  if (entry.c.includes(" stat. comp.")) {
    return "stative compound";
  }

  if (entry.c.includes(" dyn. comp.")) {
    return "dynamic compound";
  }

  if (entry.c.includes(" dyn./stat. comp.")) {
    return "dynamic or stative compound";
  }

  if (entry.c.includes(" gen. stat./dyn. comp.")) {
    return "dynamic or generative stative compound";
  }

  return "simple";
}

function getIdiosyncraticThirdMascSing(entry) {
  if (entry.tppp && entry.tppf) {
    var tpp = makePsString(entry.tppp, entry.tppf);
    var ooRes = addOoPrefix(tpp, entry);
    return {
      imperfective: tpp,
      perfective: ooRes.ps
    };
  }

  return false;
}
/**
 * Returns the roots (imperfective and perfective) of a given verb
 *
 * @param entry - the dictionary entry for the verb
 */


function getVerbRoots(entry, transitivity, complement) {
  // each of the roots compes with a short and long version
  // with or without the ending ل - ul
  var isKawulAux = entry.p === "کول";

  var shortAndLong = function shortAndLong(root, perfective) {
    var long = perfective ? root : accentOnNFromEnd(root, yulEndingInfinitive(root) ? 1 : 0);
    var short = removeEndingL(root);
    return Object.assign({
      long: long,
      short: short
    }, isKawulAux && perfective ? {
      mini: removeRetroflexR(short)
    } : {});
  };

  var infinitive = makePsString(entry.p, firstPhonetics(entry.f)); // the imperfective root is the infinitive
  // TODO: CHECK THIS!! FOR PERSON INFLECTIONS??

  var imperfective = function () {
    // if stative compound
    if (complement && spaceInForm(infinitive)) {
      var comp = complementInflects(complement) ? unisexInfToObjectMatrix(complement) : complement.masc[0][0];
      var t = getAuxTransitivity(transitivity);
      var aux = stativeAux[t].info.root.imperfective;
      return concatPsString(comp, " ", aux);
    }

    return shortAndLong(infinitive);
  }();

  var _ref = function () {
    // if stative compound
    if (complement) {
      var comp = complementInflects(complement) ? unisexInfToObjectMatrix(complement) : complement.masc[0][0];
      var t = getAuxTransitivity(transitivity);
      var aux = stativeAux[t].info.root.perfective;
      return {
        pSplit: 0,
        fSplit: 0,
        perfective: concatPsString(comp, " ", aux)
      };
    } // the perfective root is
    // - the special perfective root if it exists, or


    if (entry.prp && entry.prf) {
      var _perfective = shortAndLong(makePsString(entry.prp, entry.prf), "perfective");

      var hasOoPrefix = checkForOoPrefix(_perfective.long);
      return {
        perfective: _perfective,
        pSplit: entry.separationAtP || (hasOoPrefix ? 1 : 0),
        fSplit: entry.separationAtF || (hasOoPrefix ? 2 : 0)
      };
    } // - the infinitive prefixed with oo


    var _addOoPrefix = addOoPrefix(infinitive, entry),
        ps = _addOoPrefix.ps,
        pSplit = _addOoPrefix.pSplit,
        fSplit = _addOoPrefix.fSplit;

    return {
      perfective: shortAndLong(ps, "perfective"),
      pSplit: pSplit,
      fSplit: fSplit
    };
  }(),
      perfective = _ref.perfective,
      pSplit = _ref.pSplit,
      fSplit = _ref.fSplit;

  var perfectiveSplit = splitPerfective(perfective, pSplit, fSplit, !!complement);
  return {
    imperfective: imperfective,
    perfective: perfective,
    perfectiveSplit: perfectiveSplit
  };
}
/**
 * Returns the stems (imperfective and perfective) of a given verb
 *
 * @param entry - the dictionary entry for the verb
 */


function getVerbStems(entry, root, transitivity, complement) {
  function isRegEdulTransitive() {
    /* istanbul ignore next */
    if ("mascSing" in root.imperfective) {
      return false;
    }

    var lastPCharacters = root.imperfective.long.p.slice(-3);
    return (// @ts-ignore - will always have a entry.c if we get to this point
      entry.c.includes("intrans.") && lastPCharacters === "ېدل"
    );
  }

  function makeIntransImperfectiveStem() {
    var long = {
      // @ts-ignore
      p: root.imperfective.long.p.slice(0, -2) + "ږ",
      // @ts-ignore
      f: root.imperfective.long.f.slice(0, -4) + "éG"
    };

    if (entry.shortIntrans) {
      var short = makePsString(long.p.slice(0, -2), long.f.slice(0, -2));
      return {
        long: long,
        short: short
      };
    }

    return long;
  }

  var imperfective = function () {
    var auxTransitivity = getAuxTransitivity(transitivity);

    if (complement && spaceInForm(root.imperfective)) {
      var comp = complementInflects(complement) ? unisexInfToObjectMatrix(complement) : complement.masc[0][0];
      return concatPsString(comp, " ", stativeAux[auxTransitivity].info.stem.imperfective);
    } // the imperfective stem is
    // - the special present stem if it exists, or


    if (entry.psp && entry.psf) {
      return makePsString(entry.psp, entry.psf);
    } // - the eG form (and short form possibly) if regular transitive, or


    if (isRegEdulTransitive()) {
      return makeIntransImperfectiveStem();
    } // - the infinitive minus ل


    return "mascSing" in root.imperfective ? root.imperfective.mascSing.short : root.imperfective.short;
  }();

  var _ref2 = function () {
    if (complement) {
      var comp = complementInflects(complement) ? unisexInfToObjectMatrix(complement) : complement.masc[0][0];
      var t = getAuxTransitivity(transitivity);
      return {
        perfective: concatPsString(comp, " ", stativeAux[t].info.stem.perfective),
        pSplit: 0,
        fSplit: 0
      };
    } // the perfective stem is
    // - the special subjunctive stem if it exists, or


    if (entry.ssp && entry.ssf) {
      var isKawulAux = entry.p === "کول";

      var _perfective2 = makePsString(entry.ssp, entry.ssf);

      var hasOoPrefix = checkForOoPrefix(_perfective2);

      if (isKawulAux) {
        return {
          perfective: {
            long: _perfective2,
            short: removeRetroflexR(_perfective2)
          },
          pSplit: hasOoPrefix ? 1 : 0,
          fSplit: hasOoPrefix ? 2 : 0
        };
      }

      return {
        perfective: _perfective2,
        pSplit: entry.separationAtP || (hasOoPrefix ? 1 : 0),
        fSplit: entry.separationAtF || (hasOoPrefix ? 2 : 0)
      };
    } // - the perfective stem prefixed with oo (if possible)


    var res = addOoPrefix(imperfective, entry);
    return {
      perfective: res.ps,
      pSplit: res.pSplit,
      fSplit: res.fSplit
    };
  }(),
      perfective = _ref2.perfective,
      pSplit = _ref2.pSplit,
      fSplit = _ref2.fSplit;

  var perfectiveSplit = splitPerfective(perfective, pSplit, fSplit, !!complement);
  return Object.assign({
    imperfective: imperfective,
    perfective: perfective
  }, perfectiveSplit ? {
    perfectiveSplit: perfectiveSplit
  } : {});
}

function splitPerfective(perfective, pSplit, fSplit, isStativeComp) {
  if (!isStativeComp && pSplit === 0 && fSplit === 0) {
    return undefined;
  }

  if ("mascSing" in perfective) {
    // @ts-ignore
    return {
      mascSing: splitPerfective(perfective.mascSing, pSplit, fSplit, isStativeComp),
      mascPlur: splitPerfective(perfective.mascPlur, pSplit, fSplit, isStativeComp),
      femSing: splitPerfective(perfective.femSing, pSplit, fSplit, isStativeComp),
      femPlur: splitPerfective(perfective.femPlur, pSplit, fSplit, isStativeComp)
    };
  }

  if ("long" in perfective) {
    return Object.assign({
      // @ts-ignore
      short: splitPerfective(perfective.short, pSplit, fSplit, isStativeComp),
      long: splitPerfective(perfective.long, pSplit, fSplit, isStativeComp)
    }, "mini" in perfective ? {
      // @ts-ignore
      mini: splitPerfective(perfective.mini, pSplit, fSplit, isStativeComp)
    } : {});
  }

  if (isStativeComp) {
    // if it's a stative, split whatever is before the aux verb with a trailing space
    var pWords = perfective.p.split(" ");
    var fWords = perfective.f.split(" ");

    var _before = makePsString(pWords.slice(0, -1).join(" ") + " ", fWords.slice(0, -1).join(" ") + " ");

    var _after = makePsString(pWords[pWords.length - 1], fWords[fWords.length - 1]);

    return [_before, _after];
  }

  var pBeg = perfective.p.slice(0, pSplit);
  var before = makePsString(pBeg.endsWith(" ") ? pBeg.slice(0, -1) : pBeg, perfective.f.slice(0, fSplit).replace("`", ""));
  var beforeAccented = beginsWithDirectionalPronoun(before) ? before : accentOnFront(before);
  var after = makePsString(perfective.p.slice(pSplit), removeAccents(removeStartingTick(perfective.f.slice(fSplit))));
  return [beforeAccented, after];
}

function getParticiple(entry, stem, infinitive, transitivity, complement) {
  var shortParticipleRoot = function () {
    var shortenableEndings = ["ښتل", "ستل", "وتل"]; // special thing for اېښودل - پرېښودل

    if (infinitive.p.slice(-4) === "ښودل" && infinitive.p.length > 4 && infinitive.p !== "کېښودل" && infinitive.p !== "کښېښودل") {
      return makePsString(infinitive.p.slice(0, -3), infinitive.f.slice(0, -4));
    }

    var isOrulShortenable = ["وړل", "راوړل", "وروړل"].includes(infinitive.p);

    if (isOrulShortenable || shortenableEndings.includes(infinitive.p.slice(-3)) && infinitive.p.slice(-4) !== "استل") {
      return makePsString(infinitive.p.slice(0, -1), infinitive.f.slice(0, -2));
    }

    return null;
  }();

  var makeSepStativePart = function makeSepStativePart(complement, tense) {
    var compInflects = complementInflects(complement);
    var comp = compInflects ? unisexInfToObjectMatrix(complement) : complement.masc[0][0];
    var aux = stativeAux[auxTransitivity].info.participle[tense];
    return concatPsString(comp, " ", compInflects ? unisexInfToObjectMatrix(inflectYey(aux)) : aux);
  };

  var accentPastPart = function accentPastPart(pp) {
    return accentOnNFromEnd(pp, yulEndingInfinitive(infinitive) ? 2 : 1);
  };

  var auxTransitivity = getAuxTransitivity(transitivity);
  var past = entry.pprtp && entry.pprtf ? makePsString(entry.pprtp, entry.pprtf) : complement ? makeSepStativePart(complement, "past") : shortParticipleRoot ? {
    short: accentPastPart(concatPsString(ensureShortWurShwaShift(shortParticipleRoot), eyEndingUnaccented)),
    long: accentPastPart(concatPsString(infinitive, eyEndingUnaccented))
  } : accentPastPart(concatPsString(infinitive, eyEndingUnaccented)); // TODO: make this into a rule?

  var shortImperfectiveRoot = entry.p === "وتل" ? {
    p: "وتل",
    f: "watl"
  } : removeEndingL(infinitive);

  var accentPresPart = function accentPresPart(pp) {
    return accentOnNFromEnd(pp, 1);
  };

  var present = complement && spaceInForm(infinitive) ? makeSepStativePart(complement, "present") : shortParticipleRoot && (!psStringEquals(shortParticipleRoot, shortImperfectiveRoot) || entry.p === "وتل") ? {
    short: accentPresPart(concatPsString(shortParticipleRoot, presentParticipleSuffix)),
    long: accentPresPart(concatPsString(shortImperfectiveRoot, presentParticipleSuffix))
  } : "short" in stem.imperfective && entry.shortIntrans && entry.p !== "اوسېدل" ? {
    short: accentPresPart(concatPsString(stem.imperfective.short, presentParticipleSuffix)),
    long: accentPresPart(concatPsString(shortImperfectiveRoot, presentParticipleSuffix))
  } : accentPresPart(concatPsString(shortImperfectiveRoot.p === "وړ" ? ensureShortWurShwaShift(shortImperfectiveRoot) : shortImperfectiveRoot, presentParticipleSuffix));
  return {
    past: past,
    present: present
  };
}
/**
 * Adds a perfective و - oo prefix to a verb
 *
 * @param entry - the dictionary entry for the verb
 */


function addOoPrefix(s, entry) {
  var pSplit = 0;
  var fSplit = 0;

  function attachOo(ps) {
    if ("long" in ps) {
      return {
        short: attachOo(ps.short),
        long: attachOo(ps.long)
      };
    }

    if (entry.separationAtP && entry.separationAtF) {
      pSplit = entry.separationAtP;
      fSplit = entry.separationAtF;
      return ps;
    }

    if (entry.noOo) {
      return ps;
    }

    if (entry.sepOo) {
      pSplit = 2;
      fSplit = 3;
      return {
        p: "\u0648 ".concat(ps.p),
        f: "oo`".concat(ps.f)
      };
    }

    var startsWithA = ps.p.charAt(0) === "ا" && ps.f.charAt(0) === "a";

    if (startsWithA) {
      pSplit = 2;
      fSplit = 3;
      return {
        p: "\u0648".concat(ps.p),
        f: "wa".concat(ps.f)
      };
    }

    var startsWithAa = ["آ", "ا"].includes(ps.p.charAt(0)) && ps.f.slice(0, 2) === "aa";

    if (startsWithAa) {
      pSplit = 2;
      fSplit = 3;
      return {
        p: "\u0648\u0627".concat(ps.p.substr(1)),
        f: "w".concat(ps.f)
      };
    }

    var startsWithOo = ["óo", "oo"].includes(ps.f.slice(0, 2));

    if (startsWithOo) {
      pSplit = 1;
      fSplit = 2;
      return {
        p: "\u0648".concat(ps.p),
        f: "wU".concat(ps.f)
      };
    }

    var startsWithEe = ["ée", "ee"].includes(ps.f.slice(0, 2)) && ps.p.slice(0, 2) === "ای";
    var startsWithE = ["e", "é"].includes(ps.f[0]) && ps.p.slice(0, 2) === "اې";

    if (startsWithEe || startsWithE) {
      pSplit = 2;
      fSplit = startsWithEe ? 3 : 2;
      return {
        p: "\u0648".concat(ps.p.slice(1)),
        f: "w".concat(ps.f)
      };
    }

    var startsWithO = ["ó", "o"].includes(ps.f[0]) && ps.p.slice(0, 2) === "او";

    if (startsWithO) {
      pSplit = 1;
      fSplit = 2;
      return {
        p: "\u0648".concat(ps.p),
        f: "oo`".concat(ps.f)
      };
    }

    pSplit = 1;
    fSplit = 2;
    return {
      p: "\u0648".concat(ps.p),
      f: "oo".concat(ps.f)
    };
  }

  var attachedOo = attachOo(s);
  return {
    ps: accentOnFront(attachedOo),
    pSplit: entry.separationAtP ? entry.separationAtP : pSplit,
    fSplit: entry.separationAtF ? entry.separationAtF : fSplit
  };
}

function ensureUnisexInflections(complement) {
  var inflected = inflectWord(complement);
  var isUnisex = inflected && "masc" in inflected && "fem" in inflected;

  if (isUnisex) {
    return inflected;
  }

  var word = makePsString(complement.p, firstPhonetics(complement.f));
  return {
    masc: [[word], [word], [word]],
    fem: [[word], [word], [word]]
  };
}

function getDynamicAuxVerb(entry) {
  var auxWord = entry.p.trim().split(" ").slice(-1)[0];
  var auxWordResult = dynamicAuxVerbs.find(function (a) {
    return a.entry.p === auxWord;
  });
  /* istanbul ignore next */

  if (!auxWordResult) {
    throw new Error("unknown auxilary verb for dynamic compound");
  }

  return Object.assign({
    entry: auxWordResult.entry
  }, "complement" in auxWordResult ? {
    complement: auxWordResult.complement
  } : {});
}

function getComplementPerson(complement, usesSeperatePluralForm) {
  var number = complement.c && complement.c.includes("pl.") || usesSeperatePluralForm ? "plural" : "singular";
  var gender = complement.c && complement.c.includes("n. m.") ? "masc" : "fem";
  return getPersonNumber(gender, number);
}

function makeDynamicPerfectiveSplit(comp, auxSplit) {
  if ("mascSing" in auxSplit) {
    return {
      mascSing: makeDynamicPerfectiveSplit(comp, auxSplit.mascSing),
      mascPlur: makeDynamicPerfectiveSplit(comp, auxSplit.mascPlur),
      femSing: makeDynamicPerfectiveSplit(comp, auxSplit.femSing),
      femPlur: makeDynamicPerfectiveSplit(comp, auxSplit.femPlur)
    };
  }

  if ("long" in auxSplit) {
    return Object.assign({
      long: makeDynamicPerfectiveSplit(comp, auxSplit.long),
      short: makeDynamicPerfectiveSplit(comp, auxSplit.short)
    }, auxSplit.mini ? {
      mini: makeDynamicPerfectiveSplit(comp, auxSplit.mini)
    } : {});
  }

  return [concatPsString(comp, " ", auxSplit[0]), auxSplit[1]];
}